import { rtkQuery } from '~/services/rtkQuery';

import { TAGS } from '../tags';
import {
  CreateUserResponse,
  CreateUserVariables,
  DeactivateLinkResponse,
  DeactivateLinkVariables,
  DeleteUserResponse,
  DeleteUserVariables,
  EditUserResponse,
  EditUserVariables,
  GenerateLinkResponse,
  GenerateLinkVariables,
  GetDeletedCompaniesResponse,
  GetDeletedCompaniesVariables,
  GetExternalUsersResponse,
  GetExternalUsersVariables,
  GetUserPositionsResponse,
  GetUserResponse,
  GetUserRolesResponse,
  GetUsersResponse,
  GetUsersVariables,
  GetUserVariables,
  RestoreDeletedCompanyResponse,
  RestoreDeletedCompanyVariables,
  ValidateLinkResponse,
  ValidateLinkVariables,
} from './types';

export const adminApi = rtkQuery.injectEndpoints({
  overrideExisting: true,
  endpoints: builder => ({
    createUser: builder.mutation<CreateUserResponse, CreateUserVariables>({
      query: data => ({
        url: 'user/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [TAGS.USERS],
    }),
    getUserRoles: builder.query<GetUserRolesResponse, void>({
      query: () => 'user/roles',
    }),
    getUserPositions: builder.query<GetUserPositionsResponse, void>({
      query: () => 'user/positions',
    }),
    getUsers: builder.query<GetUsersResponse, GetUsersVariables>({
      query: data => ({
        url: 'user/list',
        method: 'GET',
        params: data,
      }),
      providesTags: [TAGS.USERS],
    }),
    getUser: builder.query<GetUserResponse, GetUserVariables>({
      query: data => `user/${data.id}`,
      providesTags: (response, error, arg) => [{ type: TAGS.USER, id: arg.id }],
    }),
    editUser: builder.mutation<EditUserResponse, EditUserVariables>({
      query: ({ id, ...data }) => ({
        url: `user/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (response, error, arg) => [TAGS.USERS, { type: TAGS.USER, id: arg.id }],
    }),
    deleteUser: builder.mutation<DeleteUserResponse, DeleteUserVariables>({
      query: data => ({
        url: `user/${data.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [TAGS.USERS],
    }),
    getDeletedCompanies: builder.query<GetDeletedCompaniesResponse, GetDeletedCompaniesVariables>({
      query: data => ({
        url: 'company/deleted/list',
        method: 'GET',
        params: data,
      }),
      providesTags: [TAGS.DELETED_COMPANIES],
    }),
    restoreDeletedCompany: builder.mutation<
      RestoreDeletedCompanyResponse,
      RestoreDeletedCompanyVariables
    >({
      query: data => ({
        url: `company/recovery/${data.id}`,
        method: 'PATCH',
      }),
      invalidatesTags: [TAGS.DELETED_COMPANIES],
    }),
    getExternalUsers: builder.query<GetExternalUsersResponse, GetExternalUsersVariables>({
      query: data => ({
        url: 'user/external/list',
        method: 'GET',
        params: data,
      }),
      providesTags: [TAGS.EXTERNAL_USERS],
    }),
    generateLink: builder.mutation<GenerateLinkResponse, GenerateLinkVariables>({
      query: ({ company_id, email, ...data }) => ({
        url: 'user/generate/link',
        method: 'POST',
        params: { company_id, email },
        body: data,
      }),
      invalidatesTags: [TAGS.EXTERNAL_USERS, TAGS.DRAFT_COMPANIES],
    }),
    validateLink: builder.mutation<ValidateLinkResponse, ValidateLinkVariables>({
      query: data => ({
        url: `user/validate/external/${data.id}`,
        method: 'POST',
        headers: { Authorization: `External ${data.id}` },
      }),
    }),
    deactivateLink: builder.mutation<DeactivateLinkResponse, DeactivateLinkVariables>({
      query: data => ({
        url: `user/deactivate/link/${data.id}`,
        method: 'PATCH',
      }),
      invalidatesTags: [TAGS.EXTERNAL_USERS],
    }),
  }),
});

export const {
  useCreateUserMutation,
  useGetUserRolesQuery,
  useGetUserPositionsQuery,
  useGetUsersQuery,
  useLazyGetUserQuery,
  useEditUserMutation,
  useDeleteUserMutation,
  useGetDeletedCompaniesQuery,
  useRestoreDeletedCompanyMutation,
  useGenerateLinkMutation,
  useGetExternalUsersQuery,
  useValidateLinkMutation,
  useDeactivateLinkMutation,
} = adminApi;
